<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import Modal from '@/components/common/layout/Modal.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { useCampaignsStore } from '@/stores/useCampaignsStore'
import * as api from '@/api/campaignsApi'

const props = defineProps<{
  open: boolean
  companyUuid: string
  /** Optional — used in the modal title for context. */
  companyName?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'added', campaignUuid: string): void
}>()

const $toast = useToast()
const store = useCampaignsStore()

type Mode = 'existing' | 'new'
const mode = ref<Mode>('existing')
const selectedCampaignUuid = ref('')
const newCampaignName = ref('')
const submitting = ref(false)

const privateCampaigns = computed(() => store.privateCampaigns)

watch(
  () => props.open,
  async (v) => {
    if (!v) return
    mode.value = 'existing'
    selectedCampaignUuid.value = ''
    newCampaignName.value = ''
    submitting.value = false
    await store.fetchPrivate({ archived: 'active' })
    // Auto-select first private if available
    if (privateCampaigns.value.length > 0) {
      selectedCampaignUuid.value = privateCampaigns.value[0].uuid
    } else {
      mode.value = 'new'
    }
  },
)

onMounted(async () => {
  if (props.open) await store.fetchPrivate({ archived: 'active' })
})

const canSubmit = computed(() => {
  if (submitting.value) return false
  if (mode.value === 'existing') return !!selectedCampaignUuid.value
  return newCampaignName.value.trim().length >= 3
})

async function handleSubmit() {
  if (!canSubmit.value) return
  submitting.value = true
  try {
    let targetUuid = selectedCampaignUuid.value
    let targetName: string | undefined

    if (mode.value === 'new') {
      const created = await store.createCampaign({
        type: 'PRIVATE',
        name: newCampaignName.value.trim(),
      })
      targetUuid = created.uuid
      targetName = created.name
    } else {
      const found = privateCampaigns.value.find((c) => c.uuid === targetUuid)
      targetName = found?.name
    }

    const result = await api.addCompaniesManual(targetUuid, {
      companyUuids: [props.companyUuid],
    })

    if (result.addedCount === 0 && result.skippedCount > 0) {
      $toast.warning('Cette société est déjà dans la campagne.')
    } else {
      $toast.success(`Société ajoutée à « ${targetName ?? 'la campagne'} ».`)
    }
    emit('added', targetUuid)
    emit('close')
  } catch (err) {
    $toast.error(`Erreur : ${err instanceof Error ? err.message : 'inconnue'}`)
  } finally {
    submitting.value = false
  }
}

const headerLabel = computed(() =>
  props.companyName ? `Ajouter « ${props.companyName} » à une campagne` : 'Ajouter à une campagne',
)
</script>

<template>
  <Modal
    :showModal="open"
    help-key="add-company-to-campaign"
    :loading="submitting"
    :title="headerLabel"
    maxWidth="lg"
    @close="emit('close')"
  >
    <template #content>
      <form class="w-full flex flex-col gap-4 p-2" @submit.prevent="handleSubmit">
        <!-- Mode toggle -->
        <div class="flex gap-2">
          <label class="flex-1 cursor-pointer">
            <input
              type="radio"
              class="hidden peer"
              value="existing"
              v-model="mode"
              :disabled="privateCampaigns.length === 0"
            />
            <div
              class="border border-base-300 rounded-lg p-3 peer-checked:border-primary peer-checked:bg-primary/10"
              :class="{ 'opacity-60': privateCampaigns.length === 0 }"
            >
              <div class="font-semibold">Campagne existante</div>
              <div class="text-xs opacity-70">
                {{ privateCampaigns.length }} privée(s) active(s)
              </div>
            </div>
          </label>
          <label class="flex-1 cursor-pointer">
            <input type="radio" class="hidden peer" value="new" v-model="mode" />
            <div
              class="border border-base-300 rounded-lg p-3 peer-checked:border-primary peer-checked:bg-primary/10"
            >
              <div class="font-semibold">Nouvelle privée</div>
              <div class="text-xs opacity-70">Crée puis ajoute</div>
            </div>
          </label>
        </div>

        <!-- Existing campaign -->
        <label v-if="mode === 'existing'" class="form-control w-full">
          <div class="label">
            <span class="label-text font-semibold">Campagne privée</span>
          </div>
          <select v-model="selectedCampaignUuid" class="select select-bordered">
            <option value="" disabled>— Choisir —</option>
            <option v-for="c in privateCampaigns" :key="c.uuid" :value="c.uuid">
              {{ c.name }} ({{ c.counters.total }} sociétés)
            </option>
          </select>
          <p
            v-if="privateCampaigns.length === 0"
            class="text-xs opacity-70 italic mt-1"
          >
            Aucune campagne privée active.
          </p>
        </label>

        <!-- New campaign -->
        <TextInput
          v-if="mode === 'new'"
          v-model="newCampaignName"
          label="Nom de la nouvelle campagne"
          placeholder="Ex : Prospects Bretagne Q2"
          required
        />

        <div class="flex justify-end gap-2 pt-2 border-t border-base-300">
          <ButtonDaisy
            type="button"
            text="Annuler"
            variant="ghost"
            :disabled="submitting"
            @click="emit('close')"
          />
          <ButtonDaisy
            type="submit"
            :text="
              submitting
                ? mode === 'new'
                  ? 'Création…'
                  : 'Ajout…'
                : mode === 'new'
                  ? 'Créer et ajouter'
                  : 'Ajouter'
            "
            variant="primary"
            :disabled="!canSubmit"
          />
        </div>
      </form>
    </template>
  </Modal>
</template>
